import { FadeIn, SectionLabel } from "@/components/Shared";
import {
  Building2,
  Package,
  Timer,
  ArrowDownUp,
  Workflow,
  Cog,
  Scissors,
  Rocket,
  Grid3x3,
  Layers,
  Sparkles,
  Palette,
  ArrowRight,
  ImageIcon,
} from "lucide-react";
import { Link } from "react-router-dom";

// Import facility images
import plantExterior from "@/assets/infrastructure/Pacfully Factory Frontview.png";
import autoLine from "@/assets/infrastructure/Rigid box Automatic machine.png";
import npdLab from "@/assets/infrastructure/Pacfully NPD Lab.png";
import semiAutoLine from "@/assets/infrastructure/Semi Autmoatic Line area.png";
import prepressArea from "@/assets/infrastructure/Pacfully Pre-press Area.png";
import corporateOffice from "@/assets/infrastructure/Pacfully Corporate Office.jpeg";
import storageArea from "@/assets/infrastructure/Storage Area.png";

const HIGHLIGHTS = [
  { icon: Building2, value: "25,000", unit: "Sq. Ft.", label: "Modern Manufacturing Facility" },
  { icon: Package, value: "8 Lakh", unit: "Boxes / month", label: "Production Capacity" },
  { icon: Timer, value: "24 Hours", unit: "Turnaround", label: "Prototype Development" },
  { icon: ArrowDownUp, value: "Low MOQ", unit: "Any volume", label: "Prototype to Mass Production" },
  { icon: Workflow, value: "End-to-End", unit: "Manufacturing", label: "Design to Dispatch" },
];

const MACHINES = [
  {
    id: "01",
    name: "Fully Automatic Line 1",
    output: "1,200 – 1,500 Boxes / Hour",
    role: "High-volume production",
    detail: "Our flagship high-speed line — engineered for consistent, large-scale rigid-box output with in-line quality checks.",
    status: "live",
  },
  {
    id: "02",
    name: "Combo Machine",
    output: "400 – 500 Boxes / Hour",
    role: "Case & box manufacturing",
    detail: "Ideal for low-volume orders — one setup, twin functions. Perfect for boutique launches and short production runs.",
    status: "live",
  },
  {
    id: "03",
    name: "Semi Automatic Line",
    output: "Flexible cycle time",
    role: "Custom & specialty jobs",
    detail: "Purpose-built for bespoke box structures where hands-on craftsmanship matters as much as throughput.",
    status: "live",
  },
  {
    id: "04",
    name: "Large Box Production Line",
    output: "Up to 600 × 550 × 220 mm",
    role: "Oversized rigid boxes",
    detail: "Custom-built line dedicated to large-format rigid boxes — hampers, subscription kits and premium gift architectures.",
    status: "live",
  },
  {
    id: "05",
    name: "Fully Automatic Line 2",
    output: "Single-pass Case & Tray / Top & Bottom",
    role: "Next-gen production",
    detail: "Coming online November 2026 — a single-pass line built to double our high-speed rigid-box capacity.",
    status: "soon",
  },
];

const CAPABILITIES = [
  { icon: Scissors, title: "Heavy-Duty Digital Cutter", desc: "Precision cutting for complex dielines, prototype boards and short runs." },
  { icon: Rocket, title: "Prototype & Sample in 24 Hours", desc: "Concept-to-hand sample turnaround — because speed is a spec." },
  { icon: Grid3x3, title: "Cutting, Creasing, Perforation & V-Grooving", desc: "In-house structural finishing across every substrate we run." },
  { icon: Layers, title: "Cold & Thermal Lamination", desc: "Matte, gloss, soft-touch and specialty film lamination on demand." },
  { icon: Sparkles, title: "Hot Foil Stamping", desc: "High-definition foil in gold, silver, holographic and custom pigments." },
  { icon: Palette, title: "Colour Proofing", desc: "Calibrated proofs for accurate Pantone and brand-colour matching before press." },
];

const GALLERY = [
  {
    title: "State-of-the-Art Plant Exterior",
    tag: "Facility",
    image: plantExterior,
    desc: "Our 25,000 sq. ft. modern manufacturing facility located in Noida, India.",
  },
  {
    title: "Fully Automatic Production Line",
    tag: "Line 01",
    image: autoLine,
    desc: "High-speed automated rigid box assembly line, delivering consistency at scale.",
  },
  {
    title: "Digital Cutting & Prototype Centre",
    tag: "R&D",
    image: npdLab,
    desc: "Our design and structural prototyping laboratory where concepts turn into tactile samples.",
  },
  {
    title: "Semi-Automatic Assembly Area",
    tag: "Production",
    image: semiAutoLine,
    desc: "Flexible production lines dedicated to bespoke designs and specialty rigid box formats.",
  },
  {
    title: "Post-press & Quality Control",
    tag: "QA",
    image: prepressArea,
    desc: "Advanced digital proofing and prepress verification to guarantee print precision.",
  },
  {
    title: "Corporate Headquarters",
    tag: "Office",
    image: corporateOffice,
    desc: "Our collaborative office space managing client relations, engineering, and logistics.",
  },
  {
    title: "Material Storage & Inventory",
    tag: "Storage",
    image: storageArea,
    desc: "Dedicated temperature-controlled storage area for FSC-certified paperboards and raw materials.",
  },
];

export default function PlantCapacity() {
  return (
    <div data-testid="plant-capacity-page">
      {/* FACILITY GALLERY (moved above Infrastructure Highlights) */}
      <section className="pt-32 md:pt-44 pb-20 md:pb-28 bg-ink-800/30 border-y border-white/5" data-testid="facility-gallery-section">
        <div className="max-w-[1400px] mx-auto px-6 md:px-10">
          <FadeIn>
            <SectionLabel>Facility gallery</SectionLabel>
            <div className="flex flex-col md:flex-row md:items-end justify-between gap-6 mt-4">
              <h2 className="font-display text-4xl md:text-5xl lg:text-6xl font-medium tracking-tight max-w-2xl leading-[1.05]">Inside the Pacfully plant.</h2>
              <p className="text-white/55 max-w-sm text-sm leading-relaxed">A visual tour of our state-of-the-art packaging plant and manufacturing departments.</p>
            </div>
          </FadeIn>

          <div className="mt-14 columns-1 sm:columns-2 lg:columns-3 gap-6">
            {GALLERY.map((g, i) => (
              <FadeIn
                key={g.title}
                delay={(i % 3) * 0.06}
                className="break-inside-avoid mb-6 block"
              >
                <div
                  data-testid={`gallery-card-${i}`}
                  className="group relative rounded-3xl overflow-hidden neo-card border border-white/10 hover:border-brand/40 transition duration-500 hover:-translate-y-1 hover:shadow-[0_0_50px_rgba(244,90,42,0.15)] flex flex-col bg-ink-900/60"
                >
                  <div className={`relative overflow-hidden ${i === 0 ? "aspect-[3/4]" : "aspect-[4/3]"}`}>
                    <img
                      src={g.image}
                      alt={g.title}
                      className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-105"
                    />
                    {g.tag && (
                      <span className="absolute top-4 left-4 text-[9px] uppercase tracking-[0.3em] text-white/85 backdrop-blur-md bg-white/5 border border-white/15 rounded-full px-3 py-1">
                        {g.tag}
                      </span>
                    )}
                  </div>
                  <div className="p-6">
                    <h3 className="font-display text-lg md:text-xl font-medium leading-tight text-white group-hover:text-brand transition duration-300">
                      {g.title}
                    </h3>
                  </div>
                </div>
              </FadeIn>
            ))}
          </div>
        </div>
      </section>

      {/* INFRASTRUCTURE HIGHLIGHTS */}
      <section className="py-20 md:py-28" data-testid="infra-highlights-section">
        <div className="max-w-[1400px] mx-auto px-6 md:px-10">
          <FadeIn>
            <SectionLabel>Infrastructure highlights</SectionLabel>
            <h2 className="font-display text-4xl md:text-5xl lg:text-6xl font-medium tracking-tight mt-4 max-w-3xl leading-[1.05]">
              Numbers that engineer confidence.
            </h2>
          </FadeIn>

          <div className="mt-14 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-5">
            {HIGHLIGHTS.map((h, i) => (
              <FadeIn key={h.label} delay={i * 0.06}>
                <div
                  data-testid={`highlight-${i}`}
                  className="group relative h-full neo-card rounded-3xl p-6 md:p-7 overflow-hidden transition-all duration-500 hover:-translate-y-1 hover:border-brand/30 hover:shadow-[0_0_40px_rgba(244,90,42,0.12)]"
                >
                  <div className="absolute -top-10 -right-10 w-32 h-32 rounded-full bg-brand/10 blur-2xl opacity-0 group-hover:opacity-100 transition duration-700 pointer-events-none" />
                  <div className="w-11 h-11 rounded-xl bg-brand/15 border border-brand/30 flex items-center justify-center">
                    <h.icon className="w-5 h-5 text-brand" />
                  </div>
                  <p className="font-display text-4xl md:text-5xl font-medium mt-6 leading-none tracking-tight text-brand">{h.value}</p>
                  <p className="text-xs uppercase tracking-[0.25em] text-white/45 mt-2">{h.unit}</p>
                  <p className="text-sm text-white/70 mt-4 leading-relaxed">{h.label}</p>
                </div>
              </FadeIn>
            ))}
          </div>
        </div>
      </section>

      {/* MANUFACTURING CAPABILITY */}
      <section className="py-20 md:py-28 bg-ink-800/30 border-y border-white/5" data-testid="machines-section">
        <div className="max-w-[1400px] mx-auto px-6 md:px-10">
          <FadeIn>
            <SectionLabel>Manufacturing capability</SectionLabel>
            <div className="flex flex-col md:flex-row md:items-end justify-between gap-6 mt-4">
              <h2 className="font-display text-4xl md:text-5xl lg:text-6xl font-medium tracking-tight max-w-2xl leading-[1.05]">Five production lines. One quality standard.</h2>
              <p className="text-white/60 max-w-sm leading-relaxed">A calibrated fleet of automatic, semi-automatic and specialty machines — sized for prototype through mass production.</p>
            </div>
          </FadeIn>

          <div className="mt-14 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {MACHINES.map((m, i) => (
              <FadeIn key={m.id} delay={(i % 3) * 0.06}>
                <div
                  data-testid={`machine-${m.id}`}
                  className={`group relative h-full neo-card rounded-3xl p-7 md:p-8 overflow-hidden transition-all duration-500 hover:-translate-y-1 ${
                    m.status === "soon" ? "border-brand/25" : "hover:border-brand/30"
                  }`}
                >
                  <div className="flex items-start justify-between">
                    <div className="w-12 h-12 rounded-2xl bg-brand/15 border border-brand/30 flex items-center justify-center">
                      <Cog className="w-5 h-5 text-brand transition group-hover:rotate-90 duration-700" />
                    </div>
                    <span className="font-display text-xs uppercase tracking-[0.28em] text-white/35">{m.id}</span>
                  </div>

                  {m.status === "soon" && (
                    <span className="mt-6 inline-flex items-center gap-2 text-[10px] uppercase tracking-[0.28em] px-3 py-1 rounded-full bg-brand/15 border border-brand/30 text-brand" data-testid={`machine-${m.id}-badge`}>
                      Coming Nov 2026
                    </span>
                  )}

                  <h3 className="font-display text-2xl md:text-[26px] font-medium mt-6 leading-tight">{m.name}</h3>
                  <p className="mt-3 text-brand text-sm font-medium">{m.output}</p>
                  <p className="mt-1 text-xs uppercase tracking-[0.22em] text-white/45">{m.role}</p>
                  <div className="mt-6 h-px bg-white/5" />
                  <p className="mt-5 text-sm text-white/65 leading-relaxed">{m.detail}</p>
                </div>
              </FadeIn>
            ))}
          </div>
        </div>
      </section>

      {/* ADVANCED CAPABILITIES */}
      <section className="py-20 md:py-28" data-testid="capabilities-section">
        <div className="max-w-[1400px] mx-auto px-6 md:px-10">
          <FadeIn>
            <SectionLabel>Advanced capabilities</SectionLabel>
            <h2 className="font-display text-4xl md:text-5xl lg:text-6xl font-medium tracking-tight mt-4 max-w-3xl leading-[1.05]">
              Every finishing craft, in-house.
            </h2>
          </FadeIn>

          <div className="mt-14 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
            {CAPABILITIES.map((c, i) => (
              <FadeIn key={c.title} delay={(i % 3) * 0.05}>
                <div
                  data-testid={`capability-${i}`}
                  className="group h-full neo-card rounded-3xl p-7 md:p-8 transition-all duration-500 hover:-translate-y-1 hover:border-brand/30 hover:shadow-[0_0_40px_rgba(244,90,42,0.12)]"
                >
                  <div className="w-12 h-12 rounded-2xl bg-brand/15 border border-brand/30 flex items-center justify-center transition group-hover:bg-brand group-hover:text-white">
                    <c.icon className="w-5 h-5 text-brand group-hover:text-white transition" />
                  </div>
                  <h3 className="font-display text-xl md:text-2xl font-medium mt-6 leading-tight">{c.title}</h3>
                  <p className="mt-3 text-sm text-white/65 leading-relaxed">{c.desc}</p>
                </div>
              </FadeIn>
            ))}
          </div>
        </div>
      </section>

      {/* FACILITY GALLERY — moved above Infrastructure Highlights */}

      {/* BOTTOM HIGHLIGHT BANNER */}
      <section className="py-24 md:py-32" data-testid="plant-cta-banner">
        <div className="max-w-[1400px] mx-auto px-6 md:px-10">
          <div className="relative neo-card rounded-[2rem] p-10 md:p-16 overflow-hidden">
            <div className="absolute -top-24 -right-24 w-[420px] h-[420px] rounded-full bg-brand/20 blur-[140px] pointer-events-none" />
            <div className="absolute -bottom-24 -left-24 w-[420px] h-[420px] rounded-full bg-brand/10 blur-[160px] pointer-events-none" />

            <div className="relative grid lg:grid-cols-[1.4fr_auto] gap-10 items-center">
              <div>
                <SectionLabel>The Pacfully promise</SectionLabel>
                <h3 className="font-display text-3xl md:text-5xl lg:text-6xl font-medium tracking-tight mt-4 leading-[1.05]">
                  From <span className="text-brand">one prototype</span> to <span className="text-brand">8 lakh rigid boxes</span> a month — our infrastructure is built to deliver precision, speed and uncompromising quality.
                </h3>
              </div>

              <div className="flex flex-col gap-3 md:flex-row lg:flex-col">
                <Link
                  to="/get-quote"
                  data-testid="plant-cta-quote"
                  className="inline-flex items-center justify-center gap-2 bg-brand text-white rounded-full px-7 py-4 text-sm font-medium hover:bg-brand-600 transition shadow-[0_8px_24px_rgba(244,90,42,0.35)]"
                >
                  Request a Quote <ArrowRight className="w-4 h-4" />
                </Link>
                <Link
                  to="/contact"
                  data-testid="plant-cta-visit"
                  className="inline-flex items-center justify-center gap-2 border border-white/15 rounded-full px-7 py-4 text-sm hover:bg-white/5 transition"
                >
                  Plan a plant visit
                </Link>
              </div>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}
